import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Loader2, Check, ChevronLeft } from "lucide-react";
import SeatGrid from "@/components/founding/SeatGrid";
import FoundingShareCard from "@/components/founding/FoundingShareCard";

const GOLD = "#E8C46A";
const pad3 = (n) => String(n).padStart(3, "0");

// The once-per-account founding-seat claim STEP at the highest-intent moment — the member's first
// authenticated session. It offers BOTH the lowest available (one-tap accept) AND "choose your own
// number" (the seat grid) as co-equal options, and every claim takes one deliberate confirmation.
// No more silent auto-assignment. Skipping calls /founding/prompt/skip so it never returns.
export default function FoundingInterstitial() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);   // prompt_state payload
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [picking, setPicking] = useState(false); // grid shown
  const [seatsData, setSeatsData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirmSeat, setConfirmSeat] = useState(null); // seat awaiting confirmation

  useEffect(() => {
    if (!user || user === true) return;
    if (sessionStorage.getItem("os_founding_interstitial_seen") === "1") return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/founding/prompt");
        if (!active) return;
        if (data?.should_interstitial) {
          setState(data);
          setOpen(true);
          sessionStorage.setItem("os_founding_interstitial_seen", "1");
        }
      } catch { /* silently skip — the persistent prompt + menu link remain */ }
    })();
    return () => { active = false; };
  }, [user]);

  const skip = async () => {
    setOpen(false);
    try { await api.post("/founding/prompt/skip"); } catch { /* best-effort */ }
  };

  const openGrid = async () => {
    setPicking(true);
    try {
      const { data } = await api.get("/founding/seats");
      setSeatsData(data);
      setSelected(data.lowest_available);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const doClaim = async (seatNumber) => {
    setBusy(true);
    try {
      const { data } = await api.post("/founding/claim", seatNumber ? { seat_number: seatNumber } : {});
      setClaimed(data.seat_number);
      setConfirmSeat(null);
      toast.success(`Founding seat #${pad3(data.seat_number)} is yours.`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setConfirmSeat(null);
      if (e.response?.status === 409 && detail?.error === "seat_taken") {
        toast.error(detail.message || "That seat just went — pick another.");
        await openGrid();  // refresh the grid, keep the member in the picker
      } else {
        toast.error(formatError(detail));
      }
    } finally { setBusy(false); }
  };

  if (!open || !state) return null;
  const remaining = state.public_remaining;
  const total = state.public_seats || 90;
  const lowest = state.lowest_available;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !claimed) skip(); else setOpen(o); }}>
      <DialogContent className="bg-surface text-foreground max-w-md p-0 overflow-hidden" style={{ borderColor: GOLD }} data-testid="founding-interstitial">
        <DialogTitle className="sr-only">Claim your founding seat</DialogTitle>
        <DialogDescription className="sr-only">You're one of OpticSwap's founding members — claim your founding seat and its perks.</DialogDescription>
        <div className="p-6">
          {claimed ? (
            <div className="text-center" data-testid="founding-interstitial-claimed">
              <div className="mx-auto w-12 h-12 flex items-center justify-center border" style={{ borderColor: GOLD }}>
                <Check className="w-6 h-6" style={{ color: GOLD }} />
              </div>
              <h2 className="mt-4 font-heading text-2xl font-bold uppercase tracking-tight">You're a founding member</h2>
              <FoundingShareCard seat={claimed} className="mt-4" />
              <p className="mt-3 text-sm font-body text-secondary-foreground">Share your number, or change it any time until it's confirmed at ID verification.</p>
              <button onClick={() => { setOpen(false); navigate("/founding"); }} data-testid="founding-interstitial-view"
                className="mt-4 w-full font-heading font-bold uppercase text-sm py-2.5 transition-colors text-black" style={{ backgroundColor: GOLD }}>
                View my seat
              </button>
            </div>
          ) : confirmSeat != null ? (
            <div data-testid="founding-interstitial-confirm">
              <div className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.22em]" style={{ color: GOLD }}>
                <Star className="w-4 h-4 fill-current" /> Confirm your seat
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold uppercase tracking-tight leading-none">Claim founding seat #{pad3(confirmSeat)}?</h2>
              <p className="mt-3 text-sm font-body text-secondary-foreground leading-relaxed">
                Your founding number is permanent — you can change it only until it's confirmed at ID verification. Claim seat <span className="font-mono" style={{ color: GOLD }}>#{pad3(confirmSeat)}</span>?
              </p>
              <div className="mt-5 flex items-center gap-3">
                <button onClick={() => doClaim(confirmSeat)} disabled={busy} data-testid="founding-interstitial-confirm-yes"
                  className="flex-1 inline-flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm py-3 transition-colors text-black disabled:opacity-60" style={{ backgroundColor: GOLD }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Claim #{pad3(confirmSeat)}
                </button>
                <button onClick={() => setConfirmSeat(null)} disabled={busy} data-testid="founding-interstitial-confirm-cancel"
                  className="px-4 py-3 font-mono uppercase text-xs text-secondary-foreground hover:text-foreground transition-colors">
                  Back
                </button>
              </div>
            </div>
          ) : picking ? (
            <div data-testid="founding-interstitial-picker">
              <button onClick={() => setPicking(false)} data-testid="founding-interstitial-back"
                className="inline-flex items-center gap-1 font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              <h2 className="mt-2 font-heading text-2xl font-bold uppercase tracking-tight leading-none">Choose your seat</h2>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{remaining} of {total} open · reserved seats aren't shown</p>
              {!seatsData ? (
                <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading seats…</div>
              ) : (
                <div className="mt-3 max-h-[300px] overflow-y-auto pr-1">
                  <SeatGrid seats={seatsData.seats} selected={selected} onSelect={setSelected} busy={busy} />
                </div>
              )}
              <button onClick={() => selected && setConfirmSeat(selected)} disabled={busy || !selected} data-testid="founding-interstitial-pick-claim"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm py-3 transition-colors text-black disabled:opacity-50" style={{ backgroundColor: GOLD }}>
                Claim #{selected ? pad3(selected) : "—"}
              </button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.22em]" style={{ color: GOLD }}>
                <Star className="w-4 h-4 fill-current" /> Founding member
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold uppercase tracking-tight leading-none">Claim your founding seat</h2>
              <p className="mt-3 text-sm font-body text-secondary-foreground leading-relaxed">
                You're early. Founding members get a permanent numbered badge across the marketplace — one of the first {total} on OpticSwap. Take the lowest open number, or choose your own.
              </p>
              <div className="mt-4 flex items-baseline gap-2 border-t border-b border-sniper py-3">
                <span className="font-heading text-3xl tabular-nums" style={{ color: GOLD }} data-testid="founding-interstitial-remaining">{remaining}</span>
                <span className="font-mono uppercase text-xs text-secondary-foreground">of {total} seats remaining</span>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => setConfirmSeat(lowest)} disabled={busy || !lowest} data-testid="founding-interstitial-claim"
                  className="inline-flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm py-3 transition-colors text-black disabled:opacity-60" style={{ backgroundColor: GOLD }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4 fill-current" />}
                  Claim #{lowest ? pad3(lowest) : "—"} (lowest)
                </button>
                <button onClick={openGrid} disabled={busy} data-testid="founding-interstitial-choose"
                  className="inline-flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm py-3 border transition-colors" style={{ borderColor: GOLD, color: GOLD }}>
                  Choose your own number
                </button>
              </div>
              <div className="mt-3 text-center">
                <button onClick={skip} disabled={busy} data-testid="founding-interstitial-skip"
                  className="font-mono uppercase text-xs text-secondary-foreground hover:text-foreground transition-colors">
                  Skip for now
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
